/**
 * Evidence API Client
 * Interfaces with orchestration-service evidence endpoints
 */

// API base URL - relative path works with vite proxy (dev) and cloudflare tunnel (prod)
const API_BASE = '/api/orchestration';

/**
 * Evidence stage types
 */
export type EvidenceStage = 'DEVELOPMENT' | 'TESTING' | 'REVIEW' | 'MERGE' | 'COMPLETION';

/**
 * Evidence types
 */
export type EvidenceType =
  | 'commits' | 'code_stats' | 'test_results' | 'review_approval' | 'merge_details' | 'issue_closed'
  | 'visual_qa_verification' | 'acceptance_criteria_validation' | 'build_result' | 'e2e_test_results' | 'repair_result';

/**
 * Validation status
 */
export type ValidationStatus = 'valid' | 'invalid' | 'pending' | 'error';

/**
 * Base evidence event
 */
export interface Evidence {
  eventId: string;
  issueId: string;
  stage: EvidenceStage;
  evidenceType: EvidenceType | string;
  timestamp: string;
  qualityScore?: number;
  validationStatus?: ValidationStatus;
  validationErrors?: string[];
  workflowId?: string;
  agentId?: string;
  evidenceData?: Record<string, unknown>;
}

/**
 * Evidence summary
 */
export interface EvidenceSummary {
  totalEvidence: number;
  validCount: number;
  invalidCount: number;
  pendingCount: number;
  avgQualityScore: number;
}

/**
 * Evidence list response
 */
export interface EvidenceListResponse {
  issueId: string;
  evidence: Evidence[];
  count: number;
  summary: EvidenceSummary;
}

/**
 * Gate status
 */
export interface GateStatus {
  allowed: boolean;
  reason: string;
  qualityScore?: number;
  requiredScore?: number;
}

/**
 * Gates response
 */
export interface GatesResponse {
  issueId: string;
  gates: Record<string, GateStatus>;
  timestamp: string;
}

/**
 * Stage lifecycle info
 */
export interface StageLifecycle {
  hasEvidence: boolean;
  evidenceCount: number;
  evidenceTypes: string[];
  gateStatus: GateStatus | null;
  lastUpdate?: string;
}

/**
 * Lifecycle response
 */
export interface LifecycleResponse {
  issueId: string;
  currentStage: string;
  lifecycle: Record<string, StageLifecycle>;
  totalEvidence: number;
  timestamp: string;
}

/**
 * Evidence upload request
 */
export interface EvidenceUploadRequest {
  type: EvidenceType;
  stage: EvidenceStage;
  data: Record<string, unknown>;
  workflowId?: string;
  agentId?: string;
}

/**
 * Issue with evidence data
 */
export interface IssueWithEvidence {
  issueId: string;
  issueTitle: string;
  issueStatus: string;
  evidenceCount: number;
  completionPercentage: number;
  gates: Record<string, GateStatus>;
  recentEvents: Evidence[];
  lastUpdated: string;
}

/**
 * Project evidence summary
 */
export interface ProjectEvidenceSummary {
  totalIssues: number;
  issuesWithEvidence: number;
  completionRate: number;
  totalEvidence: number;
  recentEvents: Evidence[];
  issueBreakdown: IssueWithEvidence[];
}

/**
 * Evidence API client
 */
export const evidenceApi = {
  /**
   * Get all evidence for an issue
   */
  async getEvidence(
    issueId: string,
    filters?: {
      stage?: EvidenceStage;
      type?: EvidenceType;
      validationStatus?: ValidationStatus;
    }
  ): Promise<EvidenceListResponse> {
    const params = new URLSearchParams();
    if (filters?.stage) params.set('stage', filters.stage);
    if (filters?.type) params.set('type', filters.type);
    if (filters?.validationStatus) params.set('validationStatus', filters.validationStatus);

    const queryString = params.toString();
    const url = `${API_BASE}/issues/${issueId}/evidence${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch evidence: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get evidence summary for a specific issue
   */
  async getEvidenceSummary(issueId: string): Promise<{
    summary: EvidenceSummary;
    lifecycle: LifecycleResponse;
    gates: GatesResponse;
  }> {
    const [evidenceData, lifecycle, gates] = await Promise.all([
      this.getEvidence(issueId),
      this.getLifecycle(issueId),
      this.getGates(issueId),
    ]);

    return {
      summary: evidenceData.summary,
      lifecycle,
      gates,
    };
  },

  /**
   * Get project-level evidence overview
   */
  async getProjectEvidence(projectId: string): Promise<ProjectEvidenceSummary> {
    const response = await fetch(`${API_BASE}/projects/${projectId}/evidence`);
    if (!response.ok) {
      throw new Error(`Failed to fetch project evidence: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get gate status for all stages of an issue
   */
  async getGates(issueId: string): Promise<GatesResponse> {
    const response = await fetch(`${API_BASE}/issues/${issueId}/gates`);
    if (!response.ok) {
      throw new Error(`Failed to fetch gates: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get full lifecycle status of an issue
   */
  async getLifecycle(issueId: string): Promise<LifecycleResponse> {
    const response = await fetch(`${API_BASE}/issues/${issueId}/lifecycle`);
    if (!response.ok) {
      throw new Error(`Failed to fetch lifecycle: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Upload evidence for an issue
   */
  async uploadEvidence(
    issueId: string,
    evidence: EvidenceUploadRequest
  ): Promise<{ success: boolean; eventId: string; validationStatus: ValidationStatus; qualityScore: number }> {
    const response = await fetch(`${API_BASE}/issues/${issueId}/evidence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(evidence),
    });
    if (!response.ok) {
      throw new Error(`Failed to upload evidence: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Complete an issue (validate all evidence and close)
   */
  async completeIssue(
    issueId: string,
    options?: { workflowId?: string; forceComplete?: boolean }
  ): Promise<{ success: boolean; issueId: string; completedAt: string; evidenceCount: number }> {
    const response = await fetch(`${API_BASE}/issues/${issueId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options || {}),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to complete issue: ${response.statusText}`);
    }
    return response.json();
  },
};

export default evidenceApi;

/* ── Sprint-scoped evidence (for telemetry Output tab) ─────── */

export interface SprintCommit {
  sha: string;
  message: string;
  files: string[];
  epicId: string;
  storyId: string;
  timestamp: string;
}

export interface SprintPullRequest {
  issueId: string;
  prNumber: number | null;
  prTitle: string | null;
  prUrl: string | null;
  status: string;
  baseBranch: string | null;
  headBranch: string | null;
  timestamp: string;
}

export interface SprintEvidenceSummary {
  totalEvidence: number;
  commits: number;
  testResults: number;
  codeStats: number;
  reviews: number;
  pullRequests: number;
  buildResults: number;
}

export interface SprintEvidence {
  sprintId: string;
  sprintName: string;
  sprintStatus: string;
  epicIds: string[];
  pullRequests: SprintPullRequest[];
  commits: SprintCommit[];
  evidence: Record<string, any[]>;
  summary: SprintEvidenceSummary;
  timestamp: string;
}

export async function fetchSprintEvidence(sprintId: string): Promise<SprintEvidence> {
  const res = await fetch(`${API_BASE}/sprints/${encodeURIComponent(sprintId)}/evidence`);
  if (!res.ok) throw new Error(`Failed to fetch sprint evidence: ${res.statusText}`);
  return res.json();
}
